import React, { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, MainTitle, Spinner } from '../../../../common';
import CollapseBlock from '../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import HtmlContentWithAntdImages from '../../../../common/Image/HtmlContentWithAntdImages.jsx';
import checkPermission from '../../../../../utils/helpers/checkPermission';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdComment from '../../../../common/Phd/PhdComment.jsx';
import PhdAddComment from '../../../../common/Phd/PhdAddComment.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';
import {
  useDeletePhdCommentMutation,
  useGetPhdThesisQuery,
  useSavePhdCommentMutation,
} from '../../../../../redux/services/phd';
import '../../../PublicPages/Phd/PhdThesisDetailPage.sass';

const PhdThesisDetail = () => {
  const { id } = useParams();
  const thesisId = Number(id);
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const [saveComment, { isLoading: isSavingComment }] =
    useSavePhdCommentMutation();
  const [deleteComment, { isLoading: isDeletingComment }] =
    useDeletePhdCommentMutation();
  const [newComment, setNewComment] = React.useState('');
  const [editingId, setEditingId] = React.useState(null);
  const [editingText, setEditingText] = React.useState('');

  const {
    data: thesis,
    isLoading,
    error: loadError,
    refetch,
  } = useGetPhdThesisQuery(thesisId, {
    skip: !thesisId,
  });
  const { studyFieldMap, isLoading: isReferenceLoading } = usePhdReferenceData({
    includeStudents: false,
    includeDepartments: false,
    includeUsers: false,
    includeExaminers: false,
  });

  useEffect(() => {
    const title = thesis?.title_sk || t('Detail');
    document.title = `${title} - FRI CMS`;
  }, [t, thesis?.title_sk]);

  const canViewComments = useMemo(
    () => checkPermission(user, 'phd-comments.index', isAdmin),
    [user, isAdmin],
  );
  const canCreateComment = useMemo(
    () => checkPermission(user, 'phd-comments.store', isAdmin),
    [user, isAdmin],
  );
  const canUpdateComment = useMemo(
    () => checkPermission(user, 'phd-comments.update', isAdmin),
    [user, isAdmin],
  );
  const canDeleteComment = useMemo(
    () => checkPermission(user, 'phd-comments.destroy', isAdmin),
    [user, isAdmin],
  );

  if (isLoading || isReferenceLoading) {
    return <Spinner />;
  }

  if (loadError || !thesis) {
    return (
      <>
        <AppBarCMS
          title={t('Detail')}
          backUrl={routes.cmsPhdTheses}
          backUrlTooltip={t('Témy dizertačných prác')}
        />
        <Alert
          type="error"
          showIcon
          title={getPhdApiErrorMessage(
            loadError,
            t('Detail dizertačnej témy sa nepodarilo načítať.'),
          )}
          style={{ margin: 10 }}
        />
      </>
    );
  }

  // `presentation` is immutable once this thesis has a frozen document
  // snapshot. Never resolve historical labels through current FK relations.
  const presentation = thesis.presentation || {};
  const isHistoricalThesis = Boolean(
    thesis.is_frozen && thesis.defended_at && !thesis.phd_student_id,
  );
  const supervisorLabel =
    presentation.supervisor ||
    thesis?.supervisor_display_name ||
    thesis?.supervisor?.full_name ||
    thesis?.supervisor?.display_name ||
    thesis?.supervisor?.email ||
    t('Nedefinovaný školiteľ');
  const specialistLabel =
    presentation.supervisorSpecialist ||
    thesis?.supervisor_specialist_display_name ||
    thesis?.supervisor_specialist?.full_name ||
    thesis?.supervisor_specialist?.display_name ||
    thesis?.supervisor_specialist?.email;
  const studentLabel =
    presentation.student ||
    thesis?.student_display_name ||
    thesis?.student?.person?.full_name ||
    thesis?.student?.person?.display_name ||
    thesis?.student?.person?.email ||
    t('Nepriradený študent');
  const studyField = thesis?.studyField || thesis?.study_field;
  const mappedStudyField = studyFieldMap.get(thesis?.phd_study_field_id);
  const liveStudyFieldLabel = mappedStudyField
    ? mappedStudyField
    : studyField
      ? studyField.focus
        ? `${studyField.study_field} - ${studyField.focus}`
        : studyField.study_field
      : t('Nezaradený odbor');
  const studyFieldLabel =
    presentation.studyField || (thesis.is_frozen ? '—' : liveStudyFieldLabel);

  const comments = thesis?.comments || [];
  const schoolYear = thesis?.schoolYear || thesis?.school_year;
  const schoolYearLabel =
    presentation.schoolYear || schoolYear
      ? presentation.schoolYear ||
        `${dayjs(schoolYear.start_year).format('YYYY')}/${dayjs(
          schoolYear.end_year,
        ).format('YYYY')}`
      : '—';
  const statusLabel = t(thesis?.status || 'pending');
  const statusTagColor = thesis?.archived_at
    ? thesis?.approved_at
      ? 'default'
      : 'red'
    : thesis?.approved_at
      ? 'green'
      : 'orange';
  const isPendingThesis = !thesis?.approved_at && !thesis?.archived_at;
  const canShowCommentsSection = canViewComments && isPendingThesis;
  const multilineCellStyle = {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
  };
  const renderRichTextCell = (value, fallback = '—') => {
    if (value === null || value === undefined) {
      return fallback;
    }

    const content = String(value).trim();
    if (!content) {
      return fallback;
    }

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(content);
    if (hasHtmlTags) {
      return <HtmlContentWithAntdImages content={content} />;
    }

    return <span style={multilineCellStyle}>{content}</span>;
  };
  const canEditComment = (comment) =>
    Boolean(
      comment &&
        (isAdmin || comment?.phd_teacher_id === user?.person_id) &&
        canUpdateComment &&
        !thesis?.approved_at,
    );
  const canRemoveComment = (comment) =>
    Boolean(
      comment &&
        (isAdmin || comment?.phd_teacher_id === user?.person_id) &&
        canDeleteComment &&
        !thesis?.approved_at,
    );
  const requiredSubjects =
    thesis?.required_study_subjects
      ?.map((subject) => subject.name)
      .filter(Boolean) || [];
  const electiveSubjects =
    thesis?.elective_study_subjects
      ?.map((subject) => subject.name)
      .filter(Boolean) || [];
  const canResolveComment = (comment) =>
    Boolean(
      comment &&
        (isAdmin || comment?.phd_teacher_id === user?.person_id) &&
        canUpdateComment &&
        !thesis?.approved_at,
    );

  const handleAddComment = async () => {
    if (!newComment.trim() || !thesis?.id) {
      return;
    }
    try {
      await saveComment({
        body: {
          phd_thesis_id: thesis.id,
          text: newComment.trim(),
        },
      }).unwrap();
      setNewComment('');
      await refetch();
    } catch (error) {
      return;
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editingText.trim()) {
      return;
    }
    try {
      await saveComment({
        id: commentId,
        body: {
          text: editingText.trim(),
        },
      }).unwrap();
      setEditingId(null);
      setEditingText('');
      await refetch();
    } catch (error) {
      return;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId).unwrap();
      await refetch();
    } catch (error) {
      return;
    }
  };

  const handleToggleCommentResolved = async (comment) => {
    if (!comment?.id || !canResolveComment(comment)) {
      return;
    }

    try {
      await saveComment({
        id: comment.id,
        body: {
          is_resolved: !comment.is_resolved,
        },
      }).unwrap();
      await refetch();
    } catch (error) {
      return;
    }
  };

  return (
    <>
      <AppBarCMS
        title={t('Detail')}
        backUrl={routes.cmsPhdTheses}
        backUrlTooltip={t('Témy dizertačných prác')}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako čítať detail dizertačnej témy?')}
          description={t(
            'Detail zobrazuje obsah témy, jej aktuálny stav, dokumenty a dostupné pripomienky. Zvoľte iba akciu zodpovedajúcu aktuálnemu stavu; historické údaje a údaje o obhájených prácach sú iba na čítanie.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-graduation-cap"
          link={routes.cmsPhdTheses}
        >
          {isHistoricalThesis && (
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message={t('Historická téma')}
              description={t(
                'Téma bola doplnená z archívnych podkladov. Údaje sú iba na čítanie a zodpovedajú stavu pri jej zaevidovaní.',
              )}
              style={{ marginBottom: 16 }}
            />
          )}
          <CollapseBlock title={t('Základné informácie')}>
            <table className="table table-striped table-bordered">
              <tbody>
                <tr>
                  <td style={{ width: '25%' }}>
                    <b>{t('Názov')}</b>
                  </td>
                  <td>
                    {presentation.title ||
                      thesis.title_sk ||
                      thesis.title_en ||
                      '—'}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Školský rok')}</b>
                  </td>
                  <td>{schoolYearLabel}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Stav')}</b>
                  </td>
                  <td>
                    <Tag color={statusTagColor}>{statusLabel}</Tag>
                  </td>
                </tr>
                {thesis.approved_at && (
                  <tr>
                    <td>
                      <b>{t('Dátum schválenia')}</b>
                    </td>
                    <td>{dayjs(thesis.approved_at).format('DD.MM.YYYY')}</td>
                  </tr>
                )}
                {thesis.archived_at && (
                  <tr>
                    <td>
                      <b>{t('Dátum archivácie')}</b>
                    </td>
                    <td>{dayjs(thesis.archived_at).format('DD.MM.YYYY')}</td>
                  </tr>
                )}
                {thesis.approved_at && (
                  <tr>
                    <td>
                      <b>{t('Študent')}</b>
                    </td>
                    <td>{studentLabel}</td>
                  </tr>
                )}
                {thesis.approved_at && (
                  <tr>
                    <td>
                      <b>{t('Školiteľ')}</b>
                    </td>
                    <td>{supervisorLabel}</td>
                  </tr>
                )}
                {thesis.approved_at && specialistLabel && (
                  <tr>
                    <td>
                      <b>{t('Špecialista')}</b>
                    </td>
                    <td>{specialistLabel}</td>
                  </tr>
                )}
                {(!isHistoricalThesis || presentation.studyField) && (
                  <tr>
                    <td>
                      <b>{t('Študijný odbor')}</b>
                    </td>
                    <td>{studyFieldLabel}</td>
                  </tr>
                )}
                {!isHistoricalThesis && (
                  <tr>
                    <td>
                      <b>{t('Forma štúdia')}</b>
                    </td>
                    <td>
                      {thesis.is_daily_study && (
                        <Tag color="blue">{t('Denné štúdium')}</Tag>
                      )}
                      {thesis.is_external_study && (
                        <Tag color="geekblue">{t('Externé štúdium')}</Tag>
                      )}
                      {!thesis.is_daily_study && !thesis.is_external_study
                        ? '—'
                        : null}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>
                    <b>{t('Povinné študijné jednotky')}</b>
                  </td>
                  <td>
                    {requiredSubjects.length
                      ? requiredSubjects.join(', ')
                      : '—'}
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Výberové študijné jednotky')}</b>
                  </td>
                  <td>
                    {electiveSubjects.length
                      ? electiveSubjects.join(', ')
                      : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </CollapseBlock>
          {(!isHistoricalThesis ||
            presentation.description ||
            presentation.scientificContribution ||
            presentation.scientificProgress ||
            presentation.researchTask ||
            presentation.solutionResults) && (
            <CollapseBlock title={t('Obsah témy')}>
              <table className="table table-striped table-bordered">
                <tbody>
                  <tr>
                    <td style={{ width: '25%' }}>
                      <b>{t('Popis práce')}</b>
                    </td>
                    <td>
                      {renderRichTextCell(
                        presentation.description ?? thesis.description_sk,
                        t('Popis nie je k dispozícii.'),
                      )}
                    </td>
                  </tr>
                  {(presentation.descriptionEn || thesis.description_en) && (
                    <tr>
                      <td>
                        <b>{t('Description (EN)')}</b>
                      </td>
                      <td>
                        {renderRichTextCell(
                          presentation.descriptionEn ?? thesis.description_en,
                        )}
                      </td>
                    </tr>
                  )}
                  {(presentation.scientificContribution ||
                    thesis.scientific_contribution) && (
                    <tr>
                      <td>
                        <b>{t('Vedecký prínos')}</b>
                      </td>
                      <td>
                        {renderRichTextCell(
                          presentation.scientificContribution ??
                            thesis.scientific_contribution,
                        )}
                      </td>
                    </tr>
                  )}
                  {(presentation.scientificProgress ||
                    thesis.scientific_progress) && (
                    <tr>
                      <td>
                        <b>{t('Vedecký pokrok')}</b>
                      </td>
                      <td>
                        {renderRichTextCell(
                          presentation.scientificProgress ??
                            thesis.scientific_progress,
                        )}
                      </td>
                    </tr>
                  )}
                  {(presentation.researchTask || thesis.research_task) && (
                    <tr>
                      <td>
                        <b>{t('Výskumná úloha')}</b>
                      </td>
                      <td>
                        {renderRichTextCell(
                          presentation.researchTask ?? thesis.research_task,
                        )}
                      </td>
                    </tr>
                  )}
                  {(presentation.solutionResults ||
                    thesis.solution_results) && (
                    <tr>
                      <td>
                        <b>{t('Výsledky riešenia')}</b>
                      </td>
                      <td>
                        {renderRichTextCell(
                          presentation.solutionResults ??
                            thesis.solution_results,
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CollapseBlock>
          )}
          {canShowCommentsSection && (
            <CollapseBlock title={t('Komentáre')}>
              {comments.length > 0 && (
                <div className="phd-thesis-detail__comments">
                  {comments.map((comment) => (
                    <PhdComment
                      key={comment.id}
                      comment={comment}
                      isEditing={editingId === comment.id}
                      editingText={editingText}
                      onEditingTextChange={setEditingText}
                      onEditStart={(target) => {
                        setEditingId(target.id);
                        setEditingText(target.text || '');
                      }}
                      onEditCancel={() => {
                        setEditingId(null);
                        setEditingText('');
                      }}
                      onEditSave={handleEditComment}
                      onDelete={handleDeleteComment}
                      canEdit={canEditComment(comment)}
                      canRemove={canRemoveComment(comment)}
                      canResolve={canResolveComment(comment)}
                      onToggleResolved={handleToggleCommentResolved}
                      isSaving={isSavingComment}
                      isDeleting={isDeletingComment}
                    />
                  ))}
                </div>
              )}
              {canCreateComment && (
                <section className="phd-thesis-detail__section">
                  <h3>{t('Pridať komentár')}</h3>
                  <PhdAddComment
                    value={newComment}
                    onChange={setNewComment}
                    onSubmit={handleAddComment}
                    isSaving={isSavingComment}
                  />
                </section>
              )}
            </CollapseBlock>
          )}
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdThesisDetail;
